import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import {
  BaseComponent,
  OverlayMode,
  ProgressService,
  SearchResult,
  SnackbarService,
  SnackbarType,
  getUrlParams,
} from 'src/app/shared';
import { ClaimDataService } from 'src/app/shared/services/claim-data.service';
import { ClaimService } from '../../services/claim.service';
import { TranslationService } from '../../services/translation.service';
import { emulator } from '../../services/emulator.service';
import { InventoryService } from '../../services/inventory/inventory.service';
import { InventoryStateService } from '../../services/inventory/inventory-state.service';
import { InventoryItem } from '../../models/inventory/inventory-item';
import { mapItemToRequest } from '../../models/inventory/inventory-mapper';
import { createInventoryTranslationRequest } from '../../models/inventory/inventory-translation-request';
import { SubmitInventoryRequest } from '../../models/inventory/inventory-request';

@Component({
  selector: 'app-inventory-submission-page',
  providers: [InventoryStateService],  
  template: `
    <app-section class="tw-w-full">
      <div class="app-pl2 tw-w-full">
        <app-claim-breadcrumb></app-claim-breadcrumb>
      </div>
      <app-panel
        title="Inventory submission"
        class="tw-w-full"
        [headerLevel]="1"
        [canExpand]="false"
      >
        <app-loading-panel [state]="loading ? 'Loading' : 'Loaded'">
          <ng-template #content>
            <app-claim-header
              [summary]="claim"
              class="tw-w-full"
            ></app-claim-header>

            @if (loadFailed) {
              <app-toast type="Error" class="tw-w-full app-pt2">
                {{
                  'Your saved inventory could not be loaded. Please refresh the page to try again.'
                    | translation: translationRequest
                }}
              </app-toast>
            }

            @if (inventoryState.showAutoSubmitToast()) {
              <app-toast type="Info" class="tw-w-full app-pt2">
                {{ autoSubmitToastMessage }}
              </app-toast>
            }

            @if (inventoryState.claimClosed()) {
              <app-toast type="Warning" class="tw-w-full app-pt2">
                {{
                  'This claim is closed. You cannot add new items or submit inventory updates unless the claim is reopened.'
                    | translation: translationRequest
                }}
              </app-toast>
            }

            <app-header-label
              type="Header2"
              class="tw-w-full app-pb2 app-pt3 app-header-2 tw-font-bold"
            >
              {{ addItemSectionTitle | translation: translationRequest }}
            </app-header-label>

            <div class="tw-w-full app-pb3">
              {{
                'Please note that generic or incomplete details provided on the inventory sheet by you may result in items not being included until sufficient information is provided.'
                  | translation: translationRequest
              }}
            </div>

            <app-inventory-add-item></app-inventory-add-item>

            <div class="tw-w-full tw-text-center tw-italic app-pt3 app-pb3">
              {{
                'Select Submit completed inventory to send your inventory to your examiner once all items have been added. Items may be added or updated after submission, if required. To view any previously submitted inventories, '
                  | translation: translationRequest
              }}
              <a
                class="app-color-teal tw-font-bold"
                [routerLink]="['/upload-documents-in-documentstab']"
                [queryParams]="{
                  claimId: claimId,
                  source: source,
                  focus: 'documents',
                }"
              >
                {{ 'click here' | translation: translationRequest }} </a
              >.
            </div>

            <app-header-label
              type="Header2"
              class="tw-w-full app-pb2 app-pt3 app-header-2 tw-font-bold"
            >
              {{ 'Items' | translation: translationRequest }}
            </app-header-label>

            <app-inventory-items-list
              [items]="inventoryState.items()"
              [editingItemId]="inventoryState.editingItemId()"
              [source]="source"
              [claimId]="claimId"
              [claim]="claim"
              [readOnly]="inventoryState.claimClosed()"
              (itemsChange)="inventoryState.replaceItems($event)"
              (editItem)="onEditItem($event)"
              (inventoryModified)="inventoryState.markModified()"
            ></app-inventory-items-list>

            <app-action-row class="tw-w-full app-pt4 inventory-actions">
              <app-button
                left1
                emphasis="High"
                label="Submit completed inventory"
                [disabled]="
                  inventoryState.submitDisabled() ||
                  submitting ||
                  inventoryState.claimClosed()
                "
                (click)="dialog.open(submitTemplate)"
              ></app-button>
              <app-button
                left2
                emphasis="Low"
                label="Save for later"
                [disabled]="inventoryState.saveForLaterDisabled()"
                (click)="saveForLater()"
              ></app-button>
              <app-button
                left3
                emphasis="Low"
                label="Cancel"
                [disabled]="inventoryState.cancelDisabled()"
                (click)="openCancelPage(cancelPageTemplate, $event)"
              ></app-button>
            </app-action-row>
          </ng-template>
        </app-loading-panel>
      </app-panel>
    </app-section>

    <ng-template #saveForLaterTemplate>
      <app-dialog
        title="Confirmation"
        ariaLabel="Inventory saved confirmation dialog"
        [confirmLabel]="'Ok'"
        [showCancel]="false"
        [closeOnConfirm]="true"
        [showHeader]="true"
        [width]="500"
      >
        {{
          'Your inventory has been saved. You can return later to continue. Any saved but unsubmitted inventories will auto-submit after 10 calendar days.'
            | translation: translationRequest
        }}
      </app-dialog>
    </ng-template>

    <ng-template #submitTemplate>
      <app-dialog
        title="Submit completed inventory"
        ariaLabel="Submit completed inventory confirmation dialog"
        [confirmLabel]="'Confirm'"
        [cancelLabel]="'Cancel'"
        [closeOnConfirm]="true"
        [showHeader]="true"
        [width]="500"
        (confirmEvent)="submitConfirmed($event)"
      >
        {{
          'Press Confirm to submit your inventory to your examiner. Once submitted, your Inventory submission will be available in the upload documents list on the Claim Summary page within 72 hours. Press Cancel to return to the form.'
            | translation: translationRequest
        }}
      </app-dialog>
    </ng-template>

    <ng-template #navigateAwayTemplate>
      <app-dialog
        title="Cancel Inventory submission"
        ariaLabel="Cancel inventory submission confirmation dialog"
        [confirmLabel]="'Confirm'"
        [cancelLabel]="'Back'"
        [closeOnConfirm]="true"
        [showHeader]="true"
        [width]="500"
        (confirmEvent)="navigateAwayConfirmed($event)"
      >
        {{
          'Canceling will discard any unsaved data. Press Confirm to cancel or Back to return to the form.'
            | translation: translationRequest
        }}
      </app-dialog>
    </ng-template>

    <ng-template #cancelPageTemplate>
      <app-dialog
        title="Cancel Inventory submission"
        ariaLabel="Cancel inventory submission confirmation dialog"
        [confirmLabel]="'Confirm'"
        [cancelLabel]="'Back'"
        [closeOnConfirm]="true"
        [showHeader]="true"
        [width]="500"
        (confirmEvent)="cancelPageConfirmed($event)"
      >
        {{
          'Canceling will discard any unsaved data. Press Confirm to cancel or Back to return to the form.'
            | translation: translationRequest
        }}
      </app-dialog>
    </ng-template>
  `,
  styles: [
    `
      .inventory-actions ::ng-deep > div {
        flex-wrap: wrap;
        row-gap: 8px;
      }

      .inventory-actions ::ng-deep button.text {
        height: auto;
        min-height: var(--mat-button-filled-container-height);
        white-space: normal;
        padding-top: 8px;
        padding-bottom: 8px;
      }
    `,
  ],
  standalone: false,
})
export class InventorySubmissionPageComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('navigateAwayTemplate')
  navigateAwayTemplate: TemplateRef<unknown>;

  @ViewChild('saveForLaterTemplate')
  saveForLaterTemplate: TemplateRef<unknown>;

  claim: SearchResult = <SearchResult>{};
  claimId: string;
  source: string;
  loading = true;
  loadFailed = false;
  submitting = false;

  private navigationConfirmed = false;
  private cancelDialogOpen = false;
  private discardAndLeave = false;

  translationRequest = createInventoryTranslationRequest();

  constructor(
    public claimService: ClaimService,
    public inventoryState: InventoryStateService,
    private inventoryService: InventoryService,
    private claimDataService: ClaimDataService,
    private router: Router,
    public dialog: MatDialog,
    private snackbarService: SnackbarService,
    private emulatorService: emulator,
    private progressService: ProgressService,
    private translationService: TranslationService,
  ) {
    super();
  }

  get addItemSectionTitle(): string {
    return this.inventoryState.editingItemId() !== null
      ? 'Edit item'
      : 'Add item';
  }

  get autoSubmitToastMessage(): string {
    const submittedDate = this.formatToastDate(
      this.inventoryState.autoSubmittedOn(),
    );

    return this.translationService
      .getTranslationForKey(
        'Because your saved inventory was not submitted within 10 days, it was sent to your examiner on {{DATE}}. Your Inventory submission will appear in your uploaded documents within 72 hours.',
        this.translationRequest,
      )
      .replace('{{DATE}}', submittedDate);
  }

  async ngOnInit() {
    const params = getUrlParams();
    this.claimId = params.claimId;
    this.source = params.source;

    this.subs.sink = this.claimDataService.currentClaim$.subscribe((claim) => {
      if (claim) {
        this.claim = claim;
      }
    });

    if (!this.claim || Object.keys(this.claim).length === 0) {
      this.claim = await this.claimService.getSummary(
        this.source,
        this.claimId,
      );
    }

    this.claimDataService.setClaim(this.claim);
    this.inventoryState.configure(this.source, this.claimId, this.claim);

    this.FeatureLoggingWithClaim(
      'InventorySubmission',
      '',
      'View Inventory Submission',
      this.claim,
    );

    try {
      await this.inventoryState.load();
    } catch {
      this.loadFailed = true;
    }

    this.loading = false;
  }

  override ngOnDestroy(): void {
    this.inventoryState.dispose();
    super.ngOnDestroy();
  }

  canDeactivate(): Promise<boolean> | boolean {
    if (this.discardAndLeave || !this.inventoryState.formDirty()) {
      return true;
    }

    // The Cancel button already has this confirmation open.
    if (this.cancelDialogOpen) {
      return false;
    }

    this.navigationConfirmed = false;
    const dialogRef = this.dialog.open(this.navigateAwayTemplate);

    return firstValueFrom(dialogRef.afterClosed()).then(
      () => this.navigationConfirmed,
    );
  }

  navigateAwayConfirmed(event: string) {
    this.navigationConfirmed = event === 'confirm';
  }

  onEditItem(item: InventoryItem) {
    this.inventoryState.startEditing(item.id);
  }

  async saveForLater() {
    if (this.inventoryState.saveForLaterDisabled()) {
      return;
    }

    if (this.emulatorService.showAuditorInfo()) {
      return;
    }

    if (!this.inventoryState.draftDirty()) {
      this.snackbarService.show(
        'No changes have been made.',
        SnackbarType.Info,
      );
      return;
    }

    const saved = await this.inventoryState.saveForLater();

    if (saved) {
      this.dialog.open(this.saveForLaterTemplate);
      return;
    }

    this.snackbarService.show(
      'Your inventory could not be saved. Please try again.',
      SnackbarType.Error,
    );
  }

  async submitConfirmed(event: string) {
    if (
      event !== 'confirm' ||
      this.inventoryState.submitDisabled() ||
      this.inventoryState.claimClosed()
    ) {
      return;
    }

    if (this.emulatorService.showAuditorInfo()) {
      return;
    }

    this.submitting = true;

    this.progressService.show(
      OverlayMode.Message,
      this.translationService.getTranslationForKey(
        'Submitting your inventory. This may take a moment.',
        this.translationRequest,
      ),
    );

    try {
      const ready = await this.inventoryState.flush();
      if (!ready) {
        this.snackbarService.show(
          'Your inventory could not be submitted. Please try again.',
          SnackbarType.Error,
        );
        return;
      }

      const response = await this.inventoryService.submitInventory(
        this.buildSubmitRequest(),
        this.claim,
      );

      if (response?.previewOnly) {
        this.snackbarService.show(
          response.statusDescription ??
            'Preview only mode. Inventory was not submitted.',
          SnackbarType.Info,
        );
        return;
      }

      if (
        !response ||
        response.confirmationNumber === 'FAILED' ||
        !response.pdfGenerated
      ) {
        this.snackbarService.show(
          'Your inventory could not be submitted. Please try again.',
          SnackbarType.Error,
        );
        return;
      }

      this.inventoryState.markSubmitted();

      this.snackbarService.show(
        'Thank you for submitting your request. Your inventory has been sent to your examiner for review.',
        SnackbarType.Success,
      );
    } finally {
      this.progressService.hide();
      this.submitting = false;
    }
  }

  openCancelPage(template: TemplateRef<unknown>, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.cancelDialogOpen) {
      return;
    }

    this.cancelDialogOpen = true;
    const dialogRef = this.dialog.open(template);
    this.subs.sink = dialogRef.afterClosed().subscribe(() => {
      this.cancelDialogOpen = false;
    });
  }

  async cancelPageConfirmed(event: string) {
    if (event !== 'confirm' || this.inventoryState.cancelDisabled()) {
      return;
    }

    this.discardAndLeave = true;
    this.inventoryState.setFormDirty(false);

    // A completed submission has no unsaved draft to discard until the user
    // adds, edits, or deletes an item.
    if (
      !this.inventoryState.hasSubmitted() ||
      this.inventoryState.hasPendingChangesAfterSubmit()
    ) {
      await this.inventoryState.clear();
    } else {
      this.inventoryState.dispose();
    }

    this.router.navigate(['/claims'], {
      queryParams: {
        claimId: this.claimId,
        source: this.source,
      },
    });
  }

  private buildSubmitRequest(): SubmitInventoryRequest {
    const request = new SubmitInventoryRequest();
    request.claimId = this.claim.claimId;
    request.lob = this.claim.lob;
    request.source = this.claim.source;
    request.vendorId = this.claim.vendorId;
    request.procUnit = this.claim.procUnit;
    request.claimNumber = this.claim.claimNumber;
    request.items = this.inventoryState.items().map(mapItemToRequest);

    return request;
  }

  private formatToastDate(value: Date | null): string {
    if (!value) {
      return '';
    }

    const date = new Date(value);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  }
}
